/**
 * Règles de durée de bail (location résidentielle).
 * Étudiant : minimum 9 mois.
 * Meublé (hors étudiant) : minimum 1 an (reconduction tacite).
 * Non meublé (hors étudiant) : minimum 3 ans.
 */
export type DureeBailUnite = 'mois' | 'ans';

export const DUREE_BAIL_UNITES: readonly DureeBailUnite[] = ['ans', 'mois'];

export interface DureeBailContexte {
  meuble: boolean;
  etudiant: boolean;
}

/** Minimum légal / métier exprimé en mois. */
export function dureeBailMinimumMois(ctx: DureeBailContexte): number {
  if (ctx.etudiant) {
    return 9;
  }
  return ctx.meuble ? 12 : 36;
}

export function dureeBailDefaut(ctx: DureeBailContexte): {
  valeur: number;
  unite: DureeBailUnite;
} {
  if (ctx.etudiant) {
    return { valeur: 9, unite: 'mois' };
  }
  return {
    valeur: ctx.meuble ? 1 : 3,
    unite: 'ans',
  };
}

export function dureeBailEnMois(
  valeur: number,
  unite: DureeBailUnite
): number {
  if (!Number.isFinite(valeur) || valeur <= 0) {
    return 0;
  }
  return unite === 'ans' ? valeur * 12 : valeur;
}

export function isDureeBailValide(
  valeur: number,
  unite: DureeBailUnite,
  ctx: DureeBailContexte
): boolean {
  return dureeBailEnMois(valeur, unite) >= dureeBailMinimumMois(ctx);
}

/** Sérialise pour {@code Locataire.dureeBailDto} (ex. {@code 3 ans}, {@code 9 mois}). */
export function formatDureeBailDto(
  valeur: number,
  unite: DureeBailUnite
): string {
  if (!Number.isFinite(valeur) || valeur <= 0) {
    return '';
  }
  if (unite === 'ans') {
    return valeur === 1 ? '1 an' : `${valeur} ans`;
  }
  return valeur === 1 ? '1 mois' : `${valeur} mois`;
}

/** Parse {@code Locataire.dureeBailDto} (ex. {@code 1 an}, {@code 9 mois}). */
export function parseDureeBailDto(
  raw?: string | null
): { valeur: number; unite: DureeBailUnite } | null {
  if (!raw) {
    return null;
  }
  const match = /^(\d+)\s*(an|ans|mois)$/.exec(raw.trim().toLowerCase());
  if (!match) {
    return null;
  }
  const valeur = Number(match[1]);
  if (!Number.isFinite(valeur) || valeur <= 0) {
    return null;
  }
  return {
    valeur,
    unite: match[2].startsWith('an') ? 'ans' : 'mois',
  };
}

/** Clé i18n d’aide selon le contexte. */
export function dureeBailHintKey(ctx: DureeBailContexte): string {
  if (ctx.etudiant) {
    return 'locataires.ajout.dureeBailHintEtudiant';
  }
  return ctx.meuble
    ? 'locataires.ajout.dureeBailHintMeuble'
    : 'locataires.ajout.dureeBailHintNonMeuble';
}

/** Clé i18n d’erreur de durée minimale. */
export function dureeBailErrorKey(ctx: DureeBailContexte): string {
  if (ctx.etudiant) {
    return 'locataires.ajout.dureeBailErrorEtudiant';
  }
  return ctx.meuble
    ? 'locataires.ajout.dureeBailErrorMeuble'
    : 'locataires.ajout.dureeBailErrorNonMeuble';
}
